import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Field } from "./UI";
import RequiredMods from "./RequiredMods";
import AddonInstall from "./AddonInstall";
import "./WorkshopBrowser.css";

const PAGE_SIZE = 16;
const NO_SCENARIOS = "This Workshop item does not contain playable scenarios.";
const SORTS = {
  relevance: "popularity",
  newest: "createdAt",
  popular: "popularity",
  rated: "averageRating"
};

function Dialog({ title, onClose, children }) {
  const dialog = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    dialog.current.querySelector("button")?.focus();
    const onKey = event => {
      if (event.key === "Escape") { event.preventDefault(); onClose(); }
      if (event.key !== "Tab") return;
      const controls = [...dialog.current.querySelectorAll("button:not(:disabled), a[href], input, select")];
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); previous?.focus(); };
  }, [onClose]);
  return createPortal(
    <div className="workshop-dialog-backdrop">
      <section ref={dialog} role="dialog" aria-modal="true" aria-labelledby="workshop-dialog-title" className="workshop-dialog">
        <div className="workshop-dialog-header">
          <h2 id="workshop-dialog-title">{title}</h2>
          <button type="button" onClick={onClose}>Close</button>
        </div>
        {children}
      </section>
    </div>, document.body
  );
}

function relevance(item, query) {
  const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const title = (item.title || "").toLowerCase();
  const author = (item.author || "").toLowerCase();
  return (title === query.toLowerCase().trim() ? 100 : 0)
    + words.reduce((score, word) => score + (title.includes(word) ? 10 : 0) + (author.includes(word) ? 1 : 0), 0);
}

export default function WorkshopBrowser({ onSelect, onModsImported, disabled }) {
  const [query, setQuery] = useState("");
  const [type, setType] = useState("all");
  const [sort, setSort] = useState("relevance");
  const [results, setResults] = useState([]);
  const [criteria, setCriteria] = useState(null);
  const [offset, setOffset] = useState(0);
  const [pageLength, setPageLength] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pendingItem, setPendingItem] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [modal, setModal] = useState(null);
  const [selection, setSelection] = useState(null);
  const [showRequiredMods, setShowRequiredMods] = useState(false);
  const [modsRevision, setModsRevision] = useState(0);
  const searchRequest = useRef(0);
  const itemRequest = useRef(0);
  const selectionRequest = useRef(0);
  useEffect(() => () => { searchRequest.current++; itemRequest.current++; }, []);

  const search = async (nextCriteria, nextOffset = 0) => {
    const request = ++searchRequest.current;
    itemRequest.current++;
    setPendingItem(null);
    setModal(null);
    setLoading(true);
    setFeedback(null);
    setResults([]);
    setPageLength(0);
    setCriteria(nextCriteria);
    setOffset(nextOffset);
    try {
      const { data } = await axios.get("/api/workshop/search", { params: {
        q: nextCriteria.query.trim(), type: nextCriteria.type, limit: PAGE_SIZE,
        offset: nextOffset, orderBy: SORTS[nextCriteria.sort], orderDirection: "desc"
      } });
      if (!Array.isArray(data)) throw new Error("Invalid results");
      if (request !== searchRequest.current) return;
      setPageLength(data.length);
      const filtered = nextCriteria.type === "addon" ? data.filter(asset => asset.type !== "scenario") : data;
      setResults(nextCriteria.sort === "relevance"
        ? [...filtered].sort((a, b) => relevance(b, nextCriteria.query) - relevance(a, nextCriteria.query)) : filtered);
    } catch {
      if (request === searchRequest.current) setFeedback({ error: true, message: "Workshop search failed. Please try again." });
    } finally {
      if (request === searchRequest.current) setLoading(false);
    }
  };

  const choose = (scenario, item) => {
    if (disabled || !scenario.scenarioId) return;
    onSelect(scenario.scenarioId);
    setSelection({ scenario, item, revision: ++selectionRequest.current });
    setShowRequiredMods(true);
    setModal(null);
    setFeedback({ message: "Scenario selected. Save Startup Parameters to apply." });
  };

  const openItem = async (asset, selecting) => {
    const request = ++itemRequest.current;
    setFeedback(null);
    if (selecting && asset.type !== "scenario") {
      setModal({ addon: asset });
      return;
    }
    setPendingItem(asset.workshopId);
    try {
      const { data } = await axios.get(`/api/workshop/item/${encodeURIComponent(asset.workshopId)}`);
      if (!Array.isArray(data?.scenarios)) throw new Error("Invalid item");
      if (request !== itemRequest.current) return;
      if (!selecting) setModal({ item: data, selecting: false });
      else if (!data.scenarios.length) setFeedback({ message: NO_SCENARIOS });
      else if (data.scenarios.length === 1) {
        if (!data.scenarios[0].scenarioId) setFeedback({ error: true, message: "Scenario ID is unavailable for this scenario." });
        else choose(data.scenarios[0], data);
      } else setModal({ item: data, selecting: true });
    } catch {
      if (request === itemRequest.current) setFeedback({ error: true, message: "Workshop item could not be loaded. Please try again." });
    } finally {
      if (request === itemRequest.current) setPendingItem(null);
    }
  };

  const closeModal = React.useCallback(() => setModal(null), []);

  return (
    <section className="workshop-browser" aria-labelledby="workshop-browser-heading">
      <h4 id="workshop-browser-heading">Workshop Browser</h4>
      <form className="workshop-toolbar" onSubmit={event => { event.preventDefault(); search({ query, type, sort }); }}>
        <Field label="Search Workshop" htmlFor="workshop-search">
          <input id="workshop-search" type="search" value={query} onChange={event => setQuery(event.target.value)} />
        </Field>
        <button type="submit" disabled={loading}>Search</button>
        <Field label="Type" htmlFor="workshop-type">
          <select id="workshop-type" value={type} onChange={event => setType(event.target.value)}>
            <option value="all">All</option><option value="scenario">Scenarios</option><option value="addon">Addons</option>
          </select>
        </Field>
        <Field label="Sort" htmlFor="workshop-sort">
          <select id="workshop-sort" value={sort} onChange={event => setSort(event.target.value)}>
            <option value="relevance">Relevance</option><option value="newest">Newest</option>
            <option value="popular">Most Popular</option><option value="rated">Highest Rated</option>
          </select>
        </Field>
      </form>
      {feedback && <p role={feedback.error ? "alert" : "status"} className={feedback.error ? "workshop-error" : "muted"}>{feedback.message}</p>}
      {loading && <p role="status">Searching Workshop…</p>}
      {pendingItem && <p role="status">Loading Workshop item…</p>}
      {!criteria && <p className="muted">Search the Workshop to find scenarios and addons.</p>}
      {criteria && !loading && !results.length && !feedback?.error && <p role="status">{pageLength === PAGE_SIZE
        ? "No matching items on this page. Try the next page." : "No Workshop items found."}</p>}
      <div className="workshop-results" aria-busy={loading}>
        {results.map(asset => (
          <article className="workshop-card" key={asset.workshopId} aria-label={asset.title || asset.workshopId}>
            {asset.thumbnail ? <img src={asset.thumbnail} alt={asset.title || "Workshop thumbnail"} loading="lazy" />
              : <div className="workshop-thumbnail-placeholder">No thumbnail</div>}
            <div className="workshop-card-body">
              <h5>{asset.title || "Untitled Workshop item"}</h5>
              <p>By {asset.author || "Unknown author"}</p>
              <dl>
                <div><dt>Rating</dt><dd>{asset.rating == null ? "Not available" : `${Math.round(asset.rating * 100)}%`}</dd></div>
                <div><dt>Asset Type</dt><dd>{asset.type === "scenario" ? "Scenario" : "Addon"}</dd></div>
                <div><dt>Workshop ID</dt><dd>{asset.workshopId}</dd></div>
              </dl>
              <div className="workshop-card-actions">
                <button type="button" className="button-secondary" disabled={Boolean(pendingItem)} onClick={() => openItem(asset, false)}>View Details</button>
                <button type="button" disabled={disabled || Boolean(pendingItem)} onClick={() => openItem(asset, true)}>{asset.type === "scenario" ? "Select Scenario" : "Add To Server"}</button>
              </div>
            </div>
          </article>
        ))}
      </div>
      {criteria && <div className="workshop-pagination">
        <button type="button" disabled={loading || offset === 0} onClick={() => search(criteria, offset - PAGE_SIZE)}>Previous</button>
        <span>Page {offset / PAGE_SIZE + 1}</span>
        <button type="button" disabled={loading || pageLength < PAGE_SIZE} onClick={() => search(criteria, offset + PAGE_SIZE)}>Next</button>
      </div>}
      {selection && <>
        <section className="workshop-scenario-details" aria-labelledby="workshop-scenario-details-heading">
          <h4 id="workshop-scenario-details-heading">Scenario Details</h4>
          <dl>
            <div><dt>Scenario Name</dt><dd>{selection.scenario.name || "Unnamed scenario"}</dd></div>
            <div><dt>Player Count</dt><dd>{selection.scenario.playerCount ?? "Not available"}</dd></div>
            <div><dt>Scenario ID</dt><dd>{selection.scenario.scenarioId}</dd></div>
          </dl>
        </section>
        {showRequiredMods && <RequiredMods key={`${selection.revision}:${modsRevision}`} workshopId={selection.item.workshopId}
          onImported={onModsImported} onCancel={() => setShowRequiredMods(false)} />}
      </>}
      {modal?.addon && <Dialog title="Add To Server" onClose={closeModal}>
        <AddonInstall asset={modal.addon} onCancel={closeModal} onImported={(config, count) => {
          onModsImported?.(config, count);
          setModsRevision(value => value + 1);
          setModal(null);
          setFeedback({ message: count ? `Added ${count} mods.` : "Everything is already installed." });
        }} />
      </Dialog>}
      {modal?.item && <Dialog title={modal.selecting ? "Select a Scenario" : "Workshop Item Details"} onClose={closeModal}>
        <h3>{modal.item.title}</h3>
        <p>By {modal.item.author || "Unknown author"}</p>
        <p>Workshop ID: {modal.item.workshopId}</p>
        {!modal.item.scenarios.length && <p>{NO_SCENARIOS}</p>}
        <ul className="workshop-scenario-list">
          {modal.item.scenarios.map((scenario, index) => <li key={`${scenario.scenarioId}-${index}`}>
            <dl>
              <div><dt>Scenario Name</dt><dd>{scenario.name || "Unnamed scenario"}</dd></div>
              <div><dt>Player Count</dt><dd>{scenario.playerCount ?? "Not available"}</dd></div>
              <div><dt>Scenario ID</dt><dd>{scenario.scenarioId || "Not available"}</dd></div>
            </dl>
            {modal.selecting && <button type="button" disabled={disabled || !scenario.scenarioId}
              onClick={() => choose(scenario, modal.item)}>Select {scenario.name || "scenario"}</button>}
          </li>)}
        </ul>
      </Dialog>}
    </section>
  );
}
